import { useEffect } from "react";

import { spaBuildId, type SpaBuildInfo } from "src/app/version/spaBuild";
import { reloadToUpdate } from "./reload";

const UPDATE_CHECK_INTERVAL_MS = 600_000;

async function fetchDeployedBuild(): Promise<SpaBuildInfo | null> {
  try {
    const response = await fetch(`/version.json?ts=${Date.now()}`, {
      cache: "no-store",
      headers: {
        "Cache-Control": "no-cache",
      },
    });

    if (!response.ok) {
      return null;
    }

    const data = (await response.json()) as Partial<SpaBuildInfo>;
    if (!data.version || !data.buildId) {
      return null;
    }

    return {
      version: data.version,
      buildId: data.buildId,
    };
  } catch {
    return null;
  }
}

export default function AppAutoRefresh() {
  useEffect(() => {
    let mounted = true;

    const checkForUpdate = async () => {
      const deployedBuild = await fetchDeployedBuild();
      if (!mounted || !deployedBuild || deployedBuild.buildId === spaBuildId) {
        return;
      }

      reloadToUpdate();
    };

    void checkForUpdate();
    const intervalId = window.setInterval(() => {
      void checkForUpdate();
    }, UPDATE_CHECK_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void checkForUpdate();
      }
    };

    const handleFocus = () => {
      void checkForUpdate();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      mounted = false;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  return null;
}
